import { DataTexture, LinearFilter, NoColorSpace, RGBAFormat, RepeatWrapping, UnsignedByteType } from 'three';

const N = 512;
const TEXEL = 0.25;
const SNAP = 64;

/**
 * The pearl's trail: a 128 m × 128 m window of RGBA8 at 25 cm, addressed toroidally (texel =
 * world position mod window), so terrain and grass can sample it with plain repeat-wrapped UVs.
 *   R  how deeply the pearl pressed here (0..1)
 *   GB travel direction (·0.5+0.5) — grass lies down along it
 *   A  written
 * Stamping is a few hundred texels per frame on the CPU; only the touched rows are uploaded.
 * The window follows the player; texels that scroll out are cleared so old marks never reappear.
 */
export class TrailMap {
  readonly texture: DataTexture;
  private readonly data = new Uint8Array(N * N * 4);
  private ox = Number.NaN;
  private oz = Number.NaN;
  private full = true;
  private readonly rowMin = new Int32Array(N).fill(N);
  private readonly rowMax = new Int32Array(N).fill(-1);
  private dirtyRows = false;

  constructor() {
    this.texture = new DataTexture(this.data, N, N, RGBAFormat, UnsignedByteType);
    this.texture.wrapS = this.texture.wrapT = RepeatWrapping;
    this.texture.minFilter = LinearFilter;
    this.texture.magFilter = LinearFilter;
    this.texture.generateMipmaps = false;
    this.texture.colorSpace = NoColorSpace;
    this.texture.needsUpdate = true;
  }

  get texelSize(): number {
    return TEXEL;
  }

  get windowSize(): number {
    return N * TEXEL;
  }

  /** Centre of the live window (world x, z). */
  get centerX(): number {
    return (this.ox + N / 2) * TEXEL;
  }

  get centerZ(): number {
    return (this.oz + N / 2) * TEXEL;
  }

  clear(): void {
    this.data.fill(0);
    this.full = true;
    this.ox = Number.NaN;
  }

  /** Keep the window centred on (x, z), clearing whatever scrolls out. */
  recenter(x: number, z: number): void {
    const ox = Math.floor(x / TEXEL / SNAP) * SNAP - N / 2;
    const oz = Math.floor(z / TEXEL / SNAP) * SNAP - N / 2;
    if (ox === this.ox && oz === this.oz) return;
    if (Number.isNaN(this.ox) || Math.abs(ox - this.ox) >= N || Math.abs(oz - this.oz) >= N) {
      this.data.fill(0);
    } else {
      // Columns that leave map onto the same texels the new columns will use.
      // (one span per row — at most two where it wraps — not a fill per texel)
      const clearCols = (a: number, b: number): void => {
        const ia = ((a % N) + N) % N;
        const end = ia + (b - a);
        for (let iz = 0; iz < N; iz++) {
          const row = iz * N;
          this.data.fill(0, (row + ia) * 4, (row + Math.min(end, N)) * 4);
          if (end > N) this.data.fill(0, row * 4, (row + end - N) * 4);
        }
      };
      const clearRows = (a: number, b: number): void => {
        for (let tz = a; tz < b; tz++) {
          const iz = ((tz % N) + N) % N;
          this.data.fill(0, iz * N * 4, (iz + 1) * N * 4);
        }
      };
      if (ox > this.ox) clearCols(this.ox, ox);
      else if (ox < this.ox) clearCols(ox + N, this.ox + N);
      if (oz > this.oz) clearRows(this.oz, oz);
      else if (oz < this.oz) clearRows(oz + N, this.oz + N);
    }
    this.ox = ox;
    this.oz = oz;
    this.full = true;
  }

  /**
   * Press a capsule from (x0, z0) to (x1, z1): `radius` metres wide, `depth` 0..1.
   * Keeps the deepest press and records the direction of travel.
   */
  stamp(x0: number, z0: number, x1: number, z1: number, radius: number, depth: number): void {
    if (Number.isNaN(this.ox)) return;
    const dx = x1 - x0;
    const dz = z1 - z0;
    const len2 = dx * dx + dz * dz;
    const len = Math.sqrt(len2);
    const ux = len > 1e-4 ? dx / len : 0;
    const uz = len > 1e-4 ? dz / len : 0;
    const gx = Math.round((ux * 0.5 + 0.5) * 255);
    const gz = Math.round((uz * 0.5 + 0.5) * 255);
    const tx0 = Math.max(this.ox, Math.floor((Math.min(x0, x1) - radius) / TEXEL));
    const tx1 = Math.min(this.ox + N - 1, Math.floor((Math.max(x0, x1) + radius) / TEXEL));
    const tz0 = Math.max(this.oz, Math.floor((Math.min(z0, z1) - radius) / TEXEL));
    const tz1 = Math.min(this.oz + N - 1, Math.floor((Math.max(z0, z1) + radius) / TEXEL));
    const d = this.data;
    const inner = radius * 0.35;
    for (let tz = tz0; tz <= tz1; tz++) {
      const pz = (tz + 0.5) * TEXEL;
      const iz = ((tz % N) + N) % N;
      for (let tx = tx0; tx <= tx1; tx++) {
        const px = (tx + 0.5) * TEXEL;
        let t = len2 > 1e-8 ? ((px - x0) * dx + (pz - z0) * dz) / len2 : 0;
        t = t < 0 ? 0 : t > 1 ? 1 : t;
        const ex = px - (x0 + dx * t);
        const ez = pz - (z0 + dz * t);
        const dist = Math.sqrt(ex * ex + ez * ez);
        if (dist >= radius) continue;
        const k = dist <= inner ? 1 : 1 - (dist - inner) / (radius - inner);
        const v = Math.round(k * k * (3 - 2 * k) * depth * 255);
        const ix = ((tx % N) + N) % N;
        const o = (iz * N + ix) * 4;
        if (v <= d[o]!) continue;
        d[o] = v;
        if (len > 1e-4) {
          d[o + 1] = gx;
          d[o + 2] = gz;
        } else if (dist > 1e-4) {
          // A resting press: blades splay outward around it.
          d[o + 1] = Math.round(((ex / dist) * 0.5 + 0.5) * 255);
          d[o + 2] = Math.round(((ez / dist) * 0.5 + 0.5) * 255);
        }
        d[o + 3] = 255;
        if (ix < this.rowMin[iz]!) this.rowMin[iz] = ix;
        if (ix > this.rowMax[iz]!) this.rowMax[iz] = ix;
        this.dirtyRows = true;
      }
    }
  }

  /** Upload changes (whole texture after a re-centre, otherwise just the touched row spans). */
  flush(): void {
    const t = this.texture;
    if (this.full) {
      t.clearUpdateRanges();
      t.needsUpdate = true;
      this.full = false;
      this.rowMin.fill(N);
      this.rowMax.fill(-1);
      this.dirtyRows = false;
      return;
    }
    if (!this.dirtyRows) return;
    for (let iz = 0; iz < N; iz++) {
      const a = this.rowMin[iz]!;
      const b = this.rowMax[iz]!;
      if (b < 0) continue;
      t.addUpdateRange((iz * N + a) * 4, (b - a + 1) * 4);
      this.rowMin[iz] = N;
      this.rowMax[iz] = -1;
    }
    t.needsUpdate = true;
    this.dirtyRows = false;
  }

  dispose(): void {
    this.texture.dispose();
  }
}
